import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Search, Menu, X, Globe } from "lucide-react";
import { pesquisarSalmos, getTitulo, LANGS } from "@/lib/salmos";
import { useLang } from "@/lib/i18n";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [resultados, setResultados] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const ref = useRef(null);
  const langRef = useRef(null);
  const { lang, setLang, t } = useLang();

  useEffect(() => {
    setOpen(false);
    setShowResults(false);
    setQ("");
  }, [location.pathname]);

  useEffect(() => {
    setResultados(pesquisarSalmos(q, lang).slice(0, 6));
  }, [q, lang]);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setShowResults(false);
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <header
      className="sticky top-0 z-50 border-b border-[var(--cor-borda)] bg-[var(--cor-fundo)]/85 backdrop-blur-md"
      data-testid="site-header"
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5 gap-4">
        <Link
          to="/"
          className="font-display text-2xl tracking-wide text-[var(--cor-texto)] hover:text-[var(--cor-dourado-claro)] transition-colors"
          data-testid="logo-link"
        >
          salmos<span className="text-[var(--cor-dourado)]">.pt</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8 font-display text-sm uppercase tracking-[0.22em] text-[var(--cor-texto-2)]">
          <Link to="/salmos" className="hover:text-[var(--cor-dourado-claro)] transition-colors" data-testid="nav-salmos">{t.salmos}</Link>
          <Link to="/categoria/ansiedade" className="hover:text-[var(--cor-dourado-claro)] transition-colors" data-testid="nav-categorias">{t.por_situacao}</Link>
          <Link to="/sobre" className="hover:text-[var(--cor-dourado-claro)] transition-colors" data-testid="nav-sobre">{t.sobre}</Link>
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden md:block relative" ref={ref}>
            <div className="flex items-center gap-2 border-b border-[var(--cor-borda)] px-2 py-1 focus-within:border-[var(--cor-dourado)] transition-colors">
              <Search className="h-4 w-4 text-[var(--cor-texto-2)]" />
              <input
                value={q}
                onChange={(e) => { setQ(e.target.value); setShowResults(true); }}
                onFocus={() => setShowResults(true)}
                placeholder={t.search_short}
                className="w-40 bg-transparent text-sm text-[var(--cor-texto)] placeholder:text-[var(--cor-texto-2)] outline-none"
                data-testid="header-search-input"
              />
            </div>
            {showResults && q && resultados.length > 0 && (
              <div className="absolute right-0 mt-3 w-80 rounded-sm border border-[var(--cor-borda)] bg-[var(--cor-fundo-2)] shadow-[0_0_40px_rgba(201,169,110,0.08)] overflow-hidden">
                {resultados.map((s) => (
                  <button
                    key={s.numero}
                    onClick={() => navigate(`/salmo/${s.numero}`)}
                    className="block w-full px-5 py-3 text-left hover:bg-[var(--cor-fundo-3)] transition-colors border-b border-[var(--cor-borda)] last:border-0"
                    data-testid={`search-result-${s.numero}`}
                  >
                    <div className="font-display text-[var(--cor-dourado)] text-sm tracking-widest uppercase">{t.salmo} {s.numero}</div>
                    <div className="text-[var(--cor-texto)] font-serif">{getTitulo(s, lang)}</div>
                  </button>
                ))}
              </div>
            )}
            {showResults && q && resultados.length === 0 && (
              <div className="absolute right-0 mt-3 w-80 rounded-sm border border-[var(--cor-borda)] bg-[var(--cor-fundo-2)] px-5 py-4 text-sm text-[var(--cor-texto-2)] italic font-serif">
                {t.nada_pesquisa} "{q}"
              </div>
            )}
          </div>

          {/* Language switcher */}
          <div className="relative" ref={langRef}>
            <button
              onClick={() => setLangOpen(!langOpen)}
              className="flex items-center gap-1.5 font-display text-xs uppercase tracking-[0.22em] text-[var(--cor-texto-2)] hover:text-[var(--cor-dourado-claro)] transition-colors px-3 py-2 rounded-full border border-[var(--cor-borda)]"
              data-testid="lang-toggle"
              aria-label="Language"
            >
              <Globe className="h-3.5 w-3.5" />
              {lang.toUpperCase()}
            </button>
            {langOpen && (
              <div className="absolute right-0 mt-2 w-32 rounded-sm border border-[var(--cor-borda)] bg-[var(--cor-fundo-2)] shadow-[0_0_40px_rgba(201,169,110,0.08)] overflow-hidden z-50">
                {LANGS.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => { setLang(l.code); setLangOpen(false); }}
                    className={`block w-full px-4 py-3 text-left font-display text-xs uppercase tracking-[0.22em] hover:bg-[var(--cor-fundo-3)] transition-colors border-b border-[var(--cor-borda)] last:border-0 ${
                      lang === l.code ? "text-[var(--cor-dourado)]" : "text-[var(--cor-texto-2)]"
                    }`}
                    data-testid={`lang-option-${l.code}`}
                  >
                    {l.label} <span className="font-serif normal-case tracking-normal text-[var(--cor-texto-2)]/70 ml-2">{l.nome}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            onClick={() => setOpen(!open)}
            className="md:hidden text-[var(--cor-texto)]"
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-[var(--cor-borda)] bg-[var(--cor-fundo-2)] px-6 py-6 space-y-5" data-testid="mobile-menu">
          <div className="flex items-center gap-2 border-b border-[var(--cor-borda)] pb-2">
            <Search className="h-4 w-4 text-[var(--cor-texto-2)]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t.search_short}
              className="w-full bg-transparent text-[var(--cor-texto)] placeholder:text-[var(--cor-texto-2)] outline-none"
              data-testid="mobile-search-input"
            />
          </div>
          {q && resultados.map((s) => (
            <Link key={s.numero} to={`/salmo/${s.numero}`} className="block py-2 border-b border-[var(--cor-borda)]">
              <div className="font-display text-[var(--cor-dourado)] text-xs tracking-widest uppercase">{t.salmo} {s.numero}</div>
              <div className="text-[var(--cor-texto)] font-serif">{getTitulo(s, lang)}</div>
            </Link>
          ))}
          <Link to="/salmos" className="block font-display uppercase tracking-[0.22em] text-sm text-[var(--cor-texto-2)]">{t.todos_salmos}</Link>
          <Link to="/categoria/ansiedade" className="block font-display uppercase tracking-[0.22em] text-sm text-[var(--cor-texto-2)]">{t.por_situacao}</Link>
          <Link to="/sobre" className="block font-display uppercase tracking-[0.22em] text-sm text-[var(--cor-texto-2)]">{t.sobre}</Link>
        </div>
      )}
    </header>
  );
}
